"use client";

import RTHM from "@/components/SVG/Logos/RTHM";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

export default function Header() {
    const router = useRouter();
    const [isCareOptionsOpen, setIsCareOptionsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsCareOptionsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsCareOptionsOpen(false);
        }, 150); // Small delay to allow moving to dropdown
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsCareOptionsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <header className="w-full backdrop-blur-xs">
            <div className="max-w-[1920px] mx-auto px-8 md:px-16 lg:px-[80px]">
                <div className="flex items-center justify-between h-20 py-6 pt-8">
                    {/* Logo */}
                    <Link href="/">
                        <RTHM className="h-10 w-auto" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex gap-20 items-center">
                        {/* Navigation Links */}
                        <nav className="flex gap-12 items-center font-suisse text-[17px] text-[#713a36] tracking-[-0.17px]">
                            <div
                                className="relative"
                                ref={dropdownRef}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <button
                                    onClick={() =>
                                        setIsCareOptionsOpen(!isCareOptionsOpen)
                                    }
                                    className="hover:opacity-70 transition-opacity flex items-center gap-1"
                                >
                                    Care Options
                                    <svg
                                        className={`w-4 h-4 transition-transform ${isCareOptionsOpen ? "rotate-180" : ""}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </button>

                                {/* Dropdown Menu */}
                                {isCareOptionsOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-lg shadow-lg border border-[#edddcd] overflow-hidden z-50">
                                        <Link
                                            href="#"
                                            className="block px-4 py-3 text-[15px] text-[#713a36] hover:bg-[#fbf0e6] transition-colors"
                                            onClick={() =>
                                                setIsCareOptionsOpen(false)
                                            }
                                        >
                                            RTHM Clinic
                                        </Link>
                                        <Link
                                            href="#"
                                            className="block px-4 py-3 text-[15px] text-[#713a36] hover:bg-[#fbf0e6] transition-colors"
                                            onClick={() =>
                                                setIsCareOptionsOpen(false)
                                            }
                                        >
                                            RTHM Medications
                                        </Link>
                                        <Link
                                            href="#"
                                            className="block px-4 py-3 text-[15px] text-[#713a36] hover:bg-[#fbf0e6] transition-colors"
                                            onClick={() =>
                                                setIsCareOptionsOpen(false)
                                            }
                                        >
                                            RTHM Lab Testing
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <Link
                                href="/team"
                                className="hover:opacity-70 transition-opacity"
                            >
                                Team
                            </Link>
                            <Link
                                href="/resources"
                                className="hover:opacity-70 transition-opacity"
                            >
                                Resources
                            </Link>
                            <Link
                                href="#"
                                className="hover:opacity-70 transition-opacity"
                            >
                                FAQ
                            </Link>
                        </nav>

                        {/* Action Buttons */}
                        <div className="flex gap-6 items-center">
                            <Button
                                variant={"outline"}
                                onClick={() => router.push("/sign-up")}
                                className="bg-transparent px-5 py-2 rounded-[100px] text-[16px] font-medium hover:bg-[#f5f5f5] transition-colors border border-[#edddcd] text-[#713a36]"
                            >
                                Login
                            </Button>

                            <Button
                                onClick={() => router.push("/sign-in")}
                                className="bg-[#713a36] text-[#fbf0e6] px-8 py-2 rounded-full text-[16px] font-medium hover:bg-[#562724] transition-colors"
                            >
                                Join now
                            </Button>
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <button className="md:hidden p-2 text-[#713a36]">
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </header>
    );
}
